import React, { createContext, useState, useContext } from 'react';
import astrologerService from '../services/astrologerService';
import consultationService from '../services/consultationService';
import bookingService from '../services/bookingService';

// Create consultation context
const ConsultationContext = createContext();

// Mock data for development
const mockAstrologers = [
  {
    id: 1,
    name: "K R Subramanian",
    specialization: "Vedic Astrology",
    rating: 5.0,
    experience: "25 years",
    is_online: true,
    profile_image: "https://via.placeholder.com/150",
    rate: "₹500/min",
    languages: ["English", "Tamil", "Hindi"],
    description: "Expert in Vedic astrology with 25 years of experience. Specializes in career guidance, relationship counseling, and life path predictions."
  }
];

// Consultation provider component
export const ConsultationProvider = ({ children }) => {
  const [astrologers, setAstrologers] = useState([]);
  const [selectedAstrologer, setSelectedAstrologer] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [currentConsultation, setCurrentConsultation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentConsultationId, setCurrentConsultationId] = useState(null);
  const [hasFetchedAstrologers, setHasFetchedAstrologers] = useState(false);

  const fetchAstrologers = async () => {
    if (hasFetchedAstrologers) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // For development, use mock data
      console.log('Using mock astrologer data for development');
      const mockAstrologers = [
        {
          id: 1,
          name: "K R Subramanian",
          specialization: "Vedic Astrology",
          rating: 5.0,
          experience: "25 years",
          is_online: true,
          profile_image: "https://via.placeholder.com/150",
          rate: "₹500/min",
          languages: ["English", "Tamil", "Hindi"],
          description: "Expert in Vedic astrology with 25 years of experience. Specializes in career guidance, relationship counseling, and life path predictions."
        }
      ];
      
      setAstrologers(mockAstrologers);
      setHasFetchedAstrologers(true);
      
    } catch (err) {
      console.error('Error fetching astrologers:', err);
      setError('Failed to fetch astrologers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get all astrologers with filters
  const getAstrologers = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await astrologerService.getAstrologers(filters);
      setAstrologers(response.data.astrologers);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch astrologers');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get online astrologers
  const getOnlineAstrologers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await astrologerService.getOnlineAstrologers();
      setAstrologers(response.data.astrologers);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch online astrologers');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get astrologer details
  const getAstrologerDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await astrologerService.getAstrologer(id);
      setSelectedAstrologer(response.data.astrologer);
      return response.data.astrologer;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch astrologer details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Rate an astrologer
  const rateAstrologer = async (id, rating) => {
    try {
      setLoading(true);
      setError(null);
      const response = await astrologerService.rateAstrologer(id, rating);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rate astrologer');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get user consultations
  const getUserConsultations = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await consultationService.getConsultations(filters);
      setConsultations(response.data.consultations);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch consultations');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get consultation details
  const getConsultationDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await consultationService.getConsultation(id);
      // API returns { status, data: { consultation: {...} } } or { consultation: {...} }
      const consultation = response?.data?.data?.consultation || response?.data?.consultation;
      setCurrentConsultation(consultation);
      return consultation;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch consultation details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a new consultation
  const createConsultation = async ({ astrologerId, type }) => {
    try {
      setLoading(true);
      setError(null);
      
      // For development, use mock data
      console.log('Using mock consultation data for development');
      const mockConsultation = {
        id: Date.now(),
        astrologer_id: astrologerId,
        type: 'chat',
        status: 'scheduled',
        created_at: new Date().toISOString()
      };
      
      setCurrentConsultation(mockConsultation);
      setCurrentConsultationId(mockConsultation.id);
      return mockConsultation;
      
    } catch (err) {
      console.error('Consultation creation error:', err);
      setError(err.response?.data?.error || 'Failed to create consultation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update consultation (end or cancel)
  const updateConsultation = async (id, action) => {
    try {
      setLoading(true);
      setError(null);
      const response = await consultationService.updateConsultation(id, { action });
      setCurrentConsultation(response.data.consultation);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update consultation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get consultation messages
  const getConsultationMessages = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await consultationService.getMessages(id);
      setMessages(response.data.messages);
      return response.data.messages;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch messages');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Send a message in consultation
  const sendMessage = async (message) => {
    if (!currentConsultationId) {
      throw new Error('No active consultation');
    }

    try {
      setLoading(true);
      setError(null);
      const response = await consultationService.sendMessage(currentConsultationId, message);
      setMessages(prev => [...prev, response.data.message]);
      return response.data.message;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get astrologer availability for tomorrow
  const getAstrologerAvailability = async (astrologerId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await astrologerService.getAvailability(astrologerId);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch availability');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Book a consultation slot
  const bookSlot = async (slotId, userNotes = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookingService.createBooking(slotId, userNotes);

      // Update current consultation if created
      if (response.data.consultation) {
        setCurrentConsultation(response.data.consultation);
        setCurrentConsultationId(response.data.consultation.id);
      }

      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to book slot');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get user's bookings
  const getUserBookings = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookingService.getUserBookings(filters);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch bookings');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    astrologers,
    selectedAstrologer,
    consultations,
    currentConsultation,
    messages,
    loading,
    error,
    getAstrologers,
    getOnlineAstrologers,
    getAstrologerDetails,
    rateAstrologer,
    getUserConsultations,
    getConsultationDetails,
    createConsultation,
    updateConsultation,
    getConsultationMessages,
    sendMessage,
    fetchAstrologers,
    currentConsultationId,
    setSelectedAstrologer,
    setMessages,
    getAstrologerAvailability,
    bookSlot,
    getUserBookings
  };

  return <ConsultationContext.Provider value={value}>{children}</ConsultationContext.Provider>;
};

// Custom hook to use consultation context
export const useConsultation = () => {
  const context = useContext(ConsultationContext);
  if (!context) {
    throw new Error('useConsultation must be used within a ConsultationProvider');
  }
  return context;
};
